import { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { getAdminStats, trackEvent } from "../api/userApi";
import { Users, FileText, Activity, Shield, ArrowLeft, Loader2, Mail, Layout, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../utils/context";

export default function AdminPage() {
    const { user } = useContext(UserContext);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Wait for user context to initialize
        if (!user) return;

        // Strict Admin Check
        if (user.email !== "mhmdpkts@gmail.com") {
            navigate("/");
            return;
        }

        const fetchStats = async () => {
            try {
                setIsLoading(true);
                trackEvent(user?.uid, "ADMIN_VIEWED");
                const response = await getAdminStats(user.uid);
                if (response.status) {
                    setData(response.data);
                } else {
                    console.error("Admin fetch failed:", response.message);
                }
            } catch (error) {
                console.error("Critical error fetching admin stats:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStats();
    }, [user, navigate]);

    if (isLoading) {
        return (
            <div className="h-screen bg-[#0f172a] flex items-center justify-center">
                <Loader2 className="animate-spin text-indigo-500" size={40} />
            </div>
        );
    }

    const stats = [
        { title: "Total Users", value: data?.stats?.total_users || 0, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
        { title: "Total Projects", value: data?.stats?.total_projects || 0, icon: Layout, color: "text-indigo-400", bg: "bg-indigo-500/10" },
        { title: "Analyzed Papers", value: data?.stats?.total_analyzed_items || 0, icon: FileText, color: "text-emerald-400", bg: "bg-emerald-500/10" },
        { title: "Database Load", value: "Standard", icon: Database, color: "text-amber-400", bg: "bg-amber-500/10" }
    ];

    return (
        <div className="min-h-screen bg-[#0f172a] text-white p-6 md:p-12 font-sans">
            <div className="max-w-7xl mx-auto">
                <header className="flex items-center justify-between mb-12">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate("/")}
                            className="p-2 hover:bg-white/5 rounded-full transition-colors"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight">Admin System <span className="text-indigo-500">Analytics</span></h1>
                            <p className="text-white/40 text-sm">Global application metrics and user activity monitoring</p>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold">
                        <Shield size={14} />
                        LEVEL 4 ACCESS
                    </div>
                </header>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    {stats.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-white/[0.03] border border-white/5 rounded-[2rem] p-8 backdrop-blur-xl relative overflow-hidden group"
                        >
                            <div className={`absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity`}>
                                <s.icon size={120} />
                            </div>
                            <div className={`${s.bg} ${s.color} w-12 h-12 rounded-2xl flex items-center justify-center mb-6`}>
                                <s.icon size={24} />
                            </div>
                            <p className="text-white/40 text-sm font-medium mb-1">{s.title}</p>
                            <h2 className="text-4xl font-black tracking-tighter">{s.value}</h2>
                        </motion.div>
                    ))}
                </div>

                {/* User List Table */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white/[0.03] border border-white/5 rounded-[2.5rem] overflow-hidden backdrop-blur-xl"
                >
                    <div className="p-8 border-b border-white/5 flex items-center justify-between bg-white/[0.01]">
                        <div className="flex items-center gap-3">
                            <Activity className="text-indigo-400" size={20} />
                            <h3 className="font-bold text-lg">Active Cloud Users</h3>
                        </div>
                        <div className="text-xs text-white/30 font-medium">REAL-TIME DATA</div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.2em] text-white/30 border-b border-white/5">
                                    <th className="px-8 py-6 font-black">User Access</th>
                                    <th className="px-8 py-6 font-black text-center">Projects</th>
                                    <th className="px-8 py-6 font-black text-center">Scans</th>
                                    <th className="px-8 py-6 font-black">Quotas (Report/Chat)</th>
                                    <th className="px-8 py-6 font-black text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {data?.users?.map((u, i) => (
                                    <tr key={i} className="hover:bg-white/[0.02] transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-xs ring-2 ring-white/5">
                                                    {u.email?.[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-bold text-white/90">{u.email}</div>
                                                    <div className="text-[10px] text-white/20 font-mono tracking-tighter">{u.uid}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-bold text-white/60">
                                                {u.projects}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs font-bold text-emerald-400">
                                                {u.items}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex gap-2">
                                                <span className="text-[10px] font-bold text-white/40">R: <span className="text-white">{u.quota?.report_generation || 0}</span></span>
                                                <span className="text-[10px] font-bold text-white/40">C: <span className="text-white">{u.quota?.chat_message || 0}</span></span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-black uppercase tracking-widest border border-emerald-500/20">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                                                Active
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
